import { Form, Formik } from 'formik';
import { useCallback, useState } from 'react';

import { format } from 'date-fns';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { upsertTournamentUser } from 'modules/players/store/actions';
import { mapPlayerToPlayerApplication } from 'modules/teams/domain/data-mappers/player-player-application.mapper';
import { PlayerApplication } from 'modules/teams/domain/interfaces/player-application';
import { updatePlayerApplication } from 'modules/teams/store/championship-teams';
import { UnionDropzone } from 'ud-form/components/dropzoneV2';
import UDFormInput from 'ud-form/components/input/index';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AppDispatch } from 'store/store';
import { teamPlayerValidator } from 'modules/players/ui/components/player-form';
import { birthDateFormatter } from 'ud-form/components/input/domain/formatters/birth-date-formatter';
import Modal from 'ud-ui/components/modal';

const getBirthDate = (birthDate: string) => {
  return format(new Date(birthDate), 'dd.MM.yyyy');
};

const useModalEditApplication = () => {
  // const { isOpen, defaultOpen: open, Modal, close } = useModal();
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<PlayerApplication | null>(null);
  const [teamId, setTeamId] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formValues, setFormValues] = useState<any>(null);
  const { info, isLoading } = useOrganizer();
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onOpen = useCallback(
    (player: PlayerApplication, teamId: number) => {
      setIsProcessing(false);
      setData(player);
      setTeamId(teamId);
      setFormValues({
        surname: player.player?.lastName,
        name: player.player?.firstName,
        middleName: player.player?.middleName,
        birthDate: player.player?.birthDate ? getBirthDate(player.player.birthDate) : '',
        photo: player.player?.logoUrl ? [player.player.logoUrl] : null,
      });
      open();
    },
    [open],
  );

  const handleSubmit = useCallback(
    (values: any) => {
      if (!info) {
        toast.error('Произошла ошибка, попробуйте перезагрузить страницу');
        throw new Error('Organizer info not found|info: ' + JSON.stringify(info, null, 2));
      }

      if (!teamId) {
        toast.error('Произошла ошибка, попробуйте перезагрузить страницу');
        throw new Error('Team id not found|teamId: ' + JSON.stringify(teamId, null, 2));
      }

      if (!data) {
        toast.error('Произошла ошибка, попробуйте перезагрузить страницу');
        throw new Error('Data not found|data: ' + JSON.stringify(data, null, 2));
      }

      setIsProcessing(true);
      setFormValues(values);
      dispatch(
        upsertTournamentUser({
          userId: data?.player?.id,
          tournamentId: info.id,
          name: values.name,
          surname: values.surname,
          middleName: values.middleName,
          birthDate: values.birthDate,
          height: +values.height,
          weight: +values.weight,
          photo: values.photo || null,
        }),
      )
        .unwrap()
        .then((responsePlayer) => {
          dispatch(
            updatePlayerApplication({
              teamId,
              id: data.id,
              application: {
                ...data,
                player: mapPlayerToPlayerApplication(responsePlayer).player,
              },
            }),
          );
          setIsProcessing(false);
          close();
          toast.success('Игрок успешно сохранён');
        });
    },
    [close, data, dispatch, info, teamId],
  );

  const modal = useCallback(() => {
    return (
      <Modal show={isOpen} onClose={close}>
        <Formik
          initialValues={
            formValues || {
              surname: '',
              name: '',
              middleName: '',
              birthDate: '',
              photo: '',
            }
          }
          enableReinitialize={false}
          validationSchema={teamPlayerValidator}
          onSubmit={handleSubmit}
        >
          {({ isValid }) => (
            <Form className="d-flex flex-column h-100 w-[400px]">
              <Modal.Body>
                <UDText type="title-3" style={{ fontFamily: 'SF Pro Text', marginBottom: '40px' }}>
                  Редактирование
                </UDText>
                <div className="d-flex flex-column h-100 gap-4 px-8">
                  <UDFormInput title="Фамилия" name="surname" placeholder="Введите фамилию" errorVisible />
                  <UDFormInput title="Имя" name="name" placeholder="Введите имя" errorVisible />
                  <UDFormInput title="Отчество" name="middleName" placeholder="Введите отчество" errorVisible />
                  <UDFormInput
                    title="Дата раждения"
                    name="birthDate"
                    placeholder="Укажите дату рождения"
                    mask="дд.мм.гггг"
                    formatter={birthDateFormatter}
                    parser={birthDateFormatter}
                    errorVisible
                  />
                  <UnionDropzone name="photo" className="w-100" />
                </div>
              </Modal.Body>
              <Modal.Footer>
                <UDButton
                  type="submit"
                  variant="primary"
                  className="mt-auto"
                  loading={isProcessing}
                  disabled={!isValid || isLoading}
                  stretch
                >
                  Сохранить
                </UDButton>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    );
  }, [close, formValues, handleSubmit, isLoading, isOpen, isProcessing]);

  return { isOpen, open: onOpen, Modal: modal };
};

export default useModalEditApplication;
